/**
 * useChatUiActions — the applyUiActions cluster extracted VERBATIM from
 * ChatBox.tsx (Wave B.2 commit 4, gated on the render char-net staying green).
 *
 * One contiguous hook slice moved as a unit, in its original relative order:
 *   1. onboardingRef            (useRef — the commit-3 indirection handle)
 *   2. applyUiActions           (useCallback → runApplyUiActions)
 *   3. onCardAction             (useCallback → runCardAction)
 *   4. conversation-command listener effect ('xavior:conversation-command')
 *   5. applyUiActionsRef        (useRef + render-phase refresh)
 *   6. 'xavior:apply-ui-actions' window-listener effect
 *   7. lastRouteModuleFocusKeyRef (useRef)
 *   8. route-module-focus effect
 *   9. openWorkspaceModuleByLabel (useCallback)
 *  10. getNextViewAckMessage     (useCallback)
 *  11. runLocalViewIntentAction  (useCallback)
 *
 * The extraction is only possible because commit 3 severed the forward
 * reference to `onboarding` (declared after useChat in ChatBox) via
 * onboardingRef — ChatBox performs the render-phase write
 * `onboardingRef.current = onboarding` right after useOnboardingManager, the
 * exact applyUiActionsRef/voiceModeRef precedent.
 *
 * DISCIPLINE: bodies are byte-identical; every dependency array is
 * byte-identical (including the dead refreshSession/updateProfile deps of
 * applyUiActions — recreate cadence is load-bearing for onCardAction and
 * MessageList re-renders; cleaning them is explicitly out of scope). The hook
 * is called at the position of the FIRST moved hook, so the global hook order
 * of ChatBox is unchanged. State (isLocalViewIntentThinking, popover/modal
 * setters) and the refs read elsewhere in ChatBox (localViewIntentTimerRef,
 * viewIntentPhraseIndexRef, workspaceModuleCatalogRef, …) stay owned by
 * ChatBox and thread in by reference.
 *
 * Pinned by ChatBox.render.test.tsx: CN-8 ('xavior:apply-ui-actions'
 * listener), CN-11 (stream → applyUiActions → onboarding/advance POST),
 * CN-13 (conversation-command focusModule + negative), CN-14 (onCardAction
 * email open / email_draft send), CN-10 (route-focus effect).
 */
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import { type ResultCardKind } from '@/features/chat/components/result-cards/types';
import type { Module } from '@/types/modules';
import {
  addWindowListener,
  conversationEvents,
  removeWindowListener,
} from '@/lib/conversation-events';
import { normalizeModuleLabelKey } from '@/features/chat/utils/chat-text';
import {
  VIEW_OPEN_ACK_TEMPLATES,
  VIEW_CREATE_ACK_TEMPLATES,
} from '../components/chatbox/constants';
import { runApplyUiActions } from '../components/chatbox/apply-ui-actions';
import { runCardAction } from '../components/chatbox/card-actions';
import type { OnboardingManagerReturn } from '../components/useOnboardingManager';

type FocusModule = (
  moduleId: string,
  options?: { anchorMessageId?: string | null; shouldScrollIntoView?: boolean },
) => void;

export interface UseChatUiActionsDeps {
  activeConversation: { id?: string; name?: string } | null | undefined;
  availableModules: Module[];
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  navigate: NavigateFunction;
  selectObject: (type: any, id: string) => void;
  // Optional `type` so the one threaded function satisfies BOTH delegate
  // signatures: runApplyUiActions wants (message, type) and runCardAction
  // wants (message, type?) — a required-2nd-param fn is not assignable to the
  // latter under strict.
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  expandChatPane: () => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  ensureModulePanelInConversationOnce: (conversationId: string, moduleId: string) => Promise<boolean>;
  refreshPanelBoundModules: (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<Module[]>;
  invalidateConversationStructure: (conversationId: string) => void;
  queuePendingModuleFocus: (focus: {
    conversationId: string;
    moduleId: string;
    anchorMessageId: string | null;
    sequence: number;
  }) => void;
  /** Dead deps of applyUiActions kept for byte-identical recreate cadence. */
  refreshSession: () => Promise<unknown> | unknown;
  updateProfile: (...args: any[]) => any;
  // Route params (route-module-focus effect)
  requestedRouteModuleId: string | null;
  routeConversationIdParam: string | undefined;
  // State setters owned by ChatBox
  setObjectCardPopover: (value: { objectType: string; objectId: string } | null) => void;
  setNoteModal: (value: { noteId: string } | null) => void;
  setDocModal: (value: { docId: string } | null) => void;
  setIsLocalViewIntentThinking: Dispatch<SetStateAction<boolean>>;
  // Refs owned by ChatBox (read/written elsewhere too)
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  moduleFocusSequenceRef: MutableRefObject<number>;
  lastAppliedModuleFocusSequenceRef: MutableRefObject<number>;
  activeConversationIdRef: MutableRefObject<string | null>;
  focusModuleRef: MutableRefObject<FocusModule>;
  viewIntentPhraseIndexRef: MutableRefObject<number>;
  localViewIntentTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

export interface UseChatUiActionsResult {
  onboardingRef: MutableRefObject<OnboardingManagerReturn | null>;
  applyUiActions: (actions: ChatUIAction[]) => void;
  applyUiActionsRef: MutableRefObject<(actions: ChatUIAction[]) => void>;
  onCardAction: (a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose'; data: any }) => unknown;
  openWorkspaceModuleByLabel: (
    label: string,
    anchorMessageId?: string,
    occurrenceIndex?: number,
  ) => Promise<void>;
  getNextViewAckMessage: (viewLabel: string, mode: 'open' | 'switch' | 'create') => string;
  runLocalViewIntentAction: (action: () => void) => void;
  lastRouteModuleFocusKeyRef: MutableRefObject<string | null>;
}

export function useChatUiActions({
  activeConversation,
  availableModules,
  token,
  workspaceId,
  queryClient,
  navigate,
  selectObject,
  showToast,
  expandChatPane,
  deleteConversation,
  refreshWorkspaceModuleCatalog,
  ensureModulePanelInConversationOnce,
  refreshPanelBoundModules,
  invalidateConversationStructure,
  queuePendingModuleFocus,
  refreshSession,
  updateProfile,
  requestedRouteModuleId,
  routeConversationIdParam,
  setObjectCardPopover,
  setNoteModal,
  setDocModal,
  setIsLocalViewIntentThinking,
  workspaceModuleCatalogRef,
  moduleFocusSequenceRef,
  lastAppliedModuleFocusSequenceRef,
  activeConversationIdRef,
  focusModuleRef,
  viewIntentPhraseIndexRef,
  localViewIntentTimerRef,
}: UseChatUiActionsDeps): UseChatUiActionsResult {
  // Render-refreshed handle to the onboarding manager (declared AFTER useChat
  // in ChatBox). applyUiActions used to close over the forward-referenced
  // `onboarding` binding directly — a memoized STALE snapshot, TDZ-safe only
  // because every invocation path is post-render. Routing the read through this
  // ref (the applyUiActionsRef / voiceModeRef / availableModulesRef precedent)
  // makes the onboarding leg always-current and severed the forward reference,
  // which is what made this cluster extractable.
  //
  // BEHAVIOR CHANGE (race-window only, Wave B.2 commit 3): a mid-stream
  // `onboarding.advance` directive now runs the CURRENT handleAdvanceDirective
  // closure (current token/workspaceId/activeConversation) instead of the
  // send-time snapshot. Concretely: if the user switches AWAY from the Welcome
  // conversation mid-stream, the Welcome-rename branch inside
  // handleAdvanceDirective is now SKIPPED (the current conversation's name is
  // not 'Welcome'), where the stale closure would still have renamed the
  // Welcome conversation. The POST /onboarding/advance body is byte-identical
  // in both worlds whenever token/workspaceId/conversation are unchanged
  // (profile?.onboardingState is a dead dep of handleAdvanceDirective — never
  // read in its body), which bounds the entire observable delta to that rename
  // branch. Ordering is unaffected: every closure version enqueues onto the
  // SAME stable onboardingAdvanceChainRef.
  const onboardingRef = useRef<OnboardingManagerReturn | null>(null);

  const applyUiActions = useCallback((actions: ChatUIAction[]) => {
    runApplyUiActions(actions, {
      activeConversation,
      availableModules,
      token,
      workspaceId,
      queryClient,
      navigate,
      selectObject,
      showToast,
      expandChatPane,
      deleteConversation,
      onboardingRef,
      refreshWorkspaceModuleCatalog,
      ensureModulePanelInConversationOnce,
      refreshPanelBoundModules,
      invalidateConversationStructure,
      queuePendingModuleFocus,
      workspaceModuleCatalogRef,
      moduleFocusSequenceRef,
      lastAppliedModuleFocusSequenceRef,
      activeConversationIdRef,
      applyUiActionsRef,
      focusModuleRef,
    });
  }, [
    activeConversation?.id,
    availableModules,
    deleteConversation,
    ensureModulePanelInConversationOnce,
    invalidateConversationStructure,
    navigate,
    queuePendingModuleFocus,
    refreshPanelBoundModules,
    refreshSession,
    selectObject,
    showToast,
    updateProfile,
  ]);

  // Result-card click handler: an inline card (e.g. a matched email) was acted
  // on. Routing logic + the four email card-action fetches are extracted into
  // chatbox/card-actions.ts (the fetches now go through lib/api/endpoints/email).
  // Delegation pattern: this useCallback keeps its exact dependency array; only
  // the body moved. setObjectCardPopover (stable setter) and
  // workspaceModuleCatalogRef (stable ref) need not be in the deps.
  const onCardAction = useCallback(
    (a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose'; data: any }) => {
      return runCardAction(a, {
        applyUiActions,
        activeConversationId: activeConversation?.id,
        showToast,
        token,
        setObjectCardPopover,
        setNoteModal,
        setDocModal,
        workspaceModuleCatalogRef,
      });
    },
    [applyUiActions, activeConversation?.id, showToast, token],
  );

  // NOTE: the handler body below reads applyUiActionsRef, declared ~35 lines
  // further down. The forward reference is safe: useRef executes synchronously
  // in this same render pass, before any effect or event handler can fire. Do
  // NOT move the applyUiActionsRef declaration below this hook's return — the
  // handler would then throw at event time.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        action?: string;
        moduleId?: string;
        conversationId?: string;
        anchorMessageId?: string | null;
        source?: string;
      } | undefined;

      if (!detail || detail.source === 'chatbox') return;
      if (detail.action !== 'focusModule' || typeof detail.moduleId !== 'string') return;
      if (detail.moduleId.startsWith('plugin:')) return;
      if (detail.conversationId && activeConversation?.id && detail.conversationId !== activeConversation.id) return;

      const anchorMessageId = typeof detail.anchorMessageId === 'string'
        ? detail.anchorMessageId
        : undefined;
      applyUiActionsRef.current([{
        type: 'module.focus',
        moduleId: detail.moduleId,
        conversationId: detail.conversationId ?? activeConversation?.id ?? undefined,
        anchorMessageId,
      }]);
    };

    addWindowListener(conversationEvents.command, handler as EventListener);
    return () => {
      removeWindowListener(conversationEvents.command, handler as EventListener);
    };
  }, [activeConversation?.id]);

  // Stable ref for applyUiActions so the route-module-focus effect doesn't re-fire
  // when applyUiActions is recreated (it depends on availableModules which changes
  // on every React Query refetch, causing infinite re-render loops).
  const applyUiActionsRef = useRef(applyUiActions);
  applyUiActionsRef.current = applyUiActions;

  // Listen for external UI action requests (e.g. from notification bell click-through)
  useEffect(() => {
    const handler = (e: Event) => {
      const actions = (e as CustomEvent).detail;
      if (Array.isArray(actions)) applyUiActionsRef.current(actions);
    };
    window.addEventListener('xavior:apply-ui-actions', handler);
    return () => window.removeEventListener('xavior:apply-ui-actions', handler);
  }, []);

  const lastRouteModuleFocusKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeConversation?.id) {
      lastRouteModuleFocusKeyRef.current = null;
      return;
    }
    if (!requestedRouteModuleId || requestedRouteModuleId.startsWith('plugin:')) {
      lastRouteModuleFocusKeyRef.current = null;
      return;
    }
    if (routeConversationIdParam && routeConversationIdParam !== activeConversation.id) {
      return;
    }

    const routeKey = `${activeConversation.id}:${requestedRouteModuleId}`;
    if (lastRouteModuleFocusKeyRef.current === routeKey) return;
    lastRouteModuleFocusKeyRef.current = routeKey;

    applyUiActionsRef.current([{
      type: 'module.focus',
      moduleId: requestedRouteModuleId,
      conversationId: activeConversation.id,
    }]);
  }, [activeConversation?.id, requestedRouteModuleId, routeConversationIdParam]);

  const openWorkspaceModuleByLabel = useCallback(async (
    label: string,
    anchorMessageId?: string,
    occurrenceIndex?: number,
  ) => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return;
    const normalizedLabel = normalizeModuleLabelKey(trimmedLabel);

    const resolveMatches = (catalog: WorkspaceModule[]) => catalog.filter((module) => (
      !module.archivedAt
      && typeof module.label === 'string'
      && normalizeModuleLabelKey(module.label) === normalizedLabel
    ));

    let matches = resolveMatches(workspaceModuleCatalogRef.current);
    if (matches.length === 0) {
      const refreshed = await refreshWorkspaceModuleCatalog();
      matches = resolveMatches(refreshed);
    }

    if (matches.length === 0) {
      showToast(`Could not find a module named "${trimmedLabel}" in this workspace.`, 'info');
      return;
    }

    const explicitIndex = Number.isInteger(occurrenceIndex)
      ? Number(occurrenceIndex)
      : undefined;
    let target = matches[0];

    if (matches.length > 1) {
      if (explicitIndex !== undefined && explicitIndex >= 0 && explicitIndex < matches.length) {
        target = matches[explicitIndex];
      } else {
        // If the click did not carry a resolved occurrence index, fall back to the first
        // workspace match instead of showing an interruption toast.
        target = matches[0];
      }
    }

    applyUiActions([{
      type: 'module.focus',
      moduleId: target.id,
      anchorMessageId: anchorMessageId ?? null,
    } as ChatUIAction]);
  }, [applyUiActions, refreshWorkspaceModuleCatalog, showToast]);

  const getNextViewAckMessage = useCallback((viewLabel: string, mode: 'open' | 'switch' | 'create') => {
    const templates = mode === 'create' ? VIEW_CREATE_ACK_TEMPLATES : VIEW_OPEN_ACK_TEMPLATES;
    const nextIndex = viewIntentPhraseIndexRef.current % templates.length;
    viewIntentPhraseIndexRef.current += 1;
    return templates[nextIndex].replace('{view}', viewLabel);
  }, []);

  const runLocalViewIntentAction = useCallback((action: () => void) => {
    if (localViewIntentTimerRef.current) {
      clearTimeout(localViewIntentTimerRef.current);
      localViewIntentTimerRef.current = null;
    }

    const delayMs = 1000;
    setIsLocalViewIntentThinking(true);
    localViewIntentTimerRef.current = setTimeout(() => {
      localViewIntentTimerRef.current = null;
      setIsLocalViewIntentThinking(false);
      action();
    }, delayMs);
  }, []);

  return {
    onboardingRef,
    applyUiActions,
    applyUiActionsRef,
    onCardAction,
    openWorkspaceModuleByLabel,
    getNextViewAckMessage,
    runLocalViewIntentAction,
    lastRouteModuleFocusKeyRef,
  };
}
